import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, X, Zap, ArrowLeft, MailCheck } from "lucide-react";
import { useLinkitAuth } from "../AuthContext";
import { linkitApi, formatApiErrorDetail } from "../api";

// ------------------------- Password strength (tiny, no deps)
function passwordStrength(pw) {
  let score = 0;
  if (!pw) return { score: 0, label: "" };
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  const labels = ["Too short", "Weak", "Okay", "Good", "Strong", "Excellent"];
  return { score, label: labels[Math.min(score, 5)] };
}

// ------------------------- Login form (single step — no wizard)
function LoginForm() {
  const nav = useNavigate();
  const { login } = useLinkitAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="mt-8 space-y-4">
      <Field label="Email" testid="auth-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" required />
      <Field label="Password" testid="auth-password" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" required />
      <button data-testid="auth-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-500 py-3 text-sm font-bold transition-colors hover:bg-indigo-400 disabled:opacity-60">
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        Log in
      </button>
    </form>
  );
}

// ------------------------- Signup wizard (3 steps)
// Step 1 — email + password (validated locally before continuing)
// Step 2 — display name + username (username uniqueness checked live)
// Step 3 — success + resend option (creates account on step-2 submit)
function SignupWizard() {
  const nav = useNavigate();
  const { applyAuth } = useLinkitAuth();
  const [step, setStep] = useState(1); // 1 | 2 | 3
  const [form, setForm] = useState({ email: "", password: "", display_name: "", username: "" });
  const [loading, setLoading] = useState(false);
  const [uname, setUname] = useState({ checking: false, available: null });
  const [emailSent, setEmailSent] = useState(false);
  const [resending, setResending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "username" ? e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") : e.target.value }));
  const strength = passwordStrength(form.password);

  // Live username availability check
  useEffect(() => {
    if (!form.username) { setUname({ checking: false, available: null }); return; }
    if (form.username.length < 3) { setUname({ checking: false, available: false }); return; }
    setUname((s) => ({ ...s, checking: true }));
    const id = setTimeout(async () => {
      try {
        const { data } = await linkitApi.get(`/auth/check-username?username=${encodeURIComponent(form.username)}`);
        setUname({ checking: false, available: data.available });
      } catch { setUname({ checking: false, available: null }); }
    }, 400);
    return () => clearTimeout(id);
  }, [form.username]);

  const nextFromStep1 = (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error("Enter a valid email"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setStep(2);
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) { toast.error("Please add a display name"); return; }
    if (uname.available !== true) { toast.error("Pick an available username"); return; }
    setLoading(true);
    try {
      const { data } = await linkitApi.post("/auth/signup", form);
      applyAuth(data); // token + user go into context immediately so subsequent calls are authed
      setEmailSent(!!data.verification_email_sent);
      setStep(3);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail));
    } finally { setLoading(false); }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      await linkitApi.post("/auth/resend-verification");
      toast.success("Verification email sent");
      setEmailSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setResending(false); }
  };

  return (
    <div>
      <StepDots current={step} />
      {step === 1 && (
        <form onSubmit={nextFromStep1} className="mt-6 space-y-4">
          <Field label="Email" testid="auth-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" required autoFocus />
          <div>
            <Field label="Password" testid="auth-password" type="password" value={form.password} onChange={set("password")} placeholder="At least 6 characters" required minLength={6} />
            {form.password && (
              <div className="mt-2 flex items-center gap-2" data-testid="signup-pw-strength">
                <div className="flex flex-1 gap-1">
                  {[0,1,2,3,4].map((i) => (
                    <span key={i} className={`h-1 flex-1 rounded-full ${i < strength.score ? (strength.score <= 2 ? "bg-rose-400" : strength.score <= 3 ? "bg-amber-400" : "bg-emerald-400") : "bg-white/10"}`} />
                  ))}
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50">{strength.label}</span>
              </div>
            )}
          </div>
          <button data-testid="signup-step1-next" className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-500 py-3 text-sm font-bold hover:bg-indigo-400">
            Continue
          </button>
          <p className="text-center text-[11px] text-white/40">By continuing, you agree to our <Link to="/create/terms" className="underline">Terms</Link> and <Link to="/create/privacy" className="underline">Privacy</Link>.</p>
        </form>
      )}
      {step === 2 && (
        <form onSubmit={submitSignup} className="mt-6 space-y-4">
          <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80" data-testid="signup-step2-back">
            <ArrowLeft size={12} /> Back
          </button>
          <Field label="Display name" testid="signup-name" value={form.display_name} onChange={set("display_name")} placeholder="Jane Creator" required autoFocus />
          <div>
            <label className="text-xs font-medium text-white/60">Username</label>
            <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-white/5 px-4 focus-within:border-indigo-400">
              <span className="text-sm text-white/40">linkit.to/u/</span>
              <input data-testid="signup-username" value={form.username} onChange={set("username")} required minLength={3} maxLength={30} className="w-full bg-transparent py-3 text-sm outline-none" placeholder="janecreator" />
              {uname.checking ? <Loader2 size={16} className="animate-spin text-white/40" /> : uname.available === true ? <Check size={16} className="text-emerald-400" data-testid="signup-username-ok" /> : uname.available === false ? <X size={16} className="text-red-400" data-testid="signup-username-bad" /> : null}
            </div>
            <p className="mt-1 text-[11px] text-white/40">Lowercase letters, numbers, - or _</p>
          </div>
          <button data-testid="auth-submit" disabled={loading || uname.available !== true} className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-500 py-3 text-sm font-bold hover:bg-indigo-400 disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            Create account
          </button>
        </form>
      )}
      {step === 3 && (
        <div className="mt-6 space-y-5 text-center" data-testid="signup-step3">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-500/15 text-indigo-300">
            <MailCheck size={30} />
          </div>
          <div>
            <h2 className="text-xl font-black">You're in, {form.display_name.split(" ")[0]}!</h2>
            <p className="mt-2 text-sm text-white/60">
              {emailSent
                ? <>We sent a verification link to <span className="font-semibold text-white">{form.email}</span>. Click it to confirm your email — you can start building right away.</>
                : <>Your account is ready. We couldn't send a verification email just now, but you can start building — try re-sending later from your dashboard.</>
              }
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              onClick={() => nav("/dashboard")}
              data-testid="signup-goto-dashboard"
              className="rounded-full bg-indigo-500 px-6 py-2.5 text-sm font-bold hover:bg-indigo-400"
            >
              Open my dashboard
            </button>
            <button
              onClick={resendVerification}
              disabled={resending}
              data-testid="signup-resend"
              className="flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold hover:bg-white/5 disabled:opacity-60"
            >
              {resending ? <Loader2 size={14} className="animate-spin" /> : null}
              Resend verification
            </button>
          </div>
          <p className="text-[11px] text-white/40">Didn't get it? Check spam — or resend above.</p>
        </div>
      )}
    </div>
  );
}

// ------------------------- Small helpers
function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="text-xs font-medium text-white/60">{label}</label>
      <input
        data-testid={testid}
        {...rest}
        className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm outline-none focus:border-indigo-400"
      />
    </div>
  );
}

function StepDots({ current }) {
  return (
    <div className="mt-6 flex items-center gap-2" data-testid={`signup-step-${current}`}>
      {[1, 2, 3].map((n) => (
        <div key={n} className="flex flex-1 flex-col gap-1">
          <div className={`h-1 rounded-full ${n <= current ? "bg-indigo-400" : "bg-white/10"}`} />
          <span className={`text-[9px] font-bold uppercase tracking-wider ${n <= current ? "text-white/70" : "text-white/30"}`}>
            {n === 1 ? "Account" : n === 2 ? "Profile" : "Verify"}
          </span>
        </div>
      ))}
    </div>
  );
}

// ------------------------- Page wrapper
export default function Auth({ mode }) {
  const isSignup = mode === "signup";
  const nav = useNavigate();
  const { user, ready } = useLinkitAuth();
  useEffect(() => {
    // If already signed in, skip the auth page.
    if (ready && user && !isSignup) nav("/dashboard", { replace: true });
  }, [ready, user, nav, isSignup]);

  return (
    <div className="flex min-h-screen bg-[#0b0c10] text-white">
      {/* Left brand panel */}
      <div className="hidden w-1/2 flex-col justify-between bg-gradient-to-br from-indigo-600 via-violet-700 to-fuchsia-700 p-12 lg:flex">
        <Link to="/create" className="flex items-center gap-2 text-lg font-black">
          <Zap size={22} fill="white" /> Linkit
        </Link>
        <div>
          <h2 className="text-4xl font-black leading-tight">One link.<br />Your whole world.</h2>
          <p className="mt-4 max-w-sm text-white/80">Describe what you want in plain English and let AI build your bio page in seconds.</p>
        </div>
        <p className="text-sm text-white/60">Loved by creators everywhere.</p>
      </div>

      {/* Form panel */}
      <div className="flex w-full flex-col justify-center px-6 sm:px-16 lg:w-1/2">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/create" className="mb-8 flex items-center gap-2 text-lg font-black lg:hidden">
            <Zap size={20} fill="white" /> Linkit
          </Link>
          <h1 className="text-3xl font-black">{isSignup ? "Create your account" : "Welcome back"}</h1>
          <p className="mt-2 text-sm text-white/50">{isSignup ? "Three quick steps and you're live." : "Log in to manage your page."}</p>

          {isSignup ? <SignupWizard /> : <LoginForm />}

          <p className="mt-6 text-center text-sm text-white/50">
            {isSignup ? "Already have an account? " : "New to Linkit? "}
            <Link to={isSignup ? "/login" : "/signup"} className="font-semibold text-indigo-400 hover:underline">
              {isSignup ? "Log in" : "Sign up free"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
